import { FC, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { PrivateRoute } from './PrivateRoute';
import { DashboardTemplate } from 'src/components/templates/DashboardTemplate';
import { EditPageHeader } from 'src/components/organisms/editPage/header/EditPageHeader';
import { PageLoading } from 'src/components/atoms/PageLoading';
import Dashboard from 'src/pages/dashboard';
import NotFound from 'src/pages/notfound';
import Login from 'src/pages/login/login';
import { ForgotPassword } from 'src/pages/login/forgotPassword';
import { ConfirmEmail } from 'src/pages/login/confirmEmail';
import SsoCallback from 'src/pages/login/ssoCallback';
import { useDemoAuthBootstrap } from 'src/components/hooks/useDemoAuthBootstrap';
import { PasswordSetupGate } from 'src/components/organisms/auth/PasswordSetupGate';

const Workspaces = lazy(() => import('src/pages/workspace/workspaces'));
const WorkspacesDetails = lazy(
  () => import('src/pages/workspace/workspaceDetails')
);
const CreateTasks = lazy(() => import('src/pages/createTasks'));
const Profile = lazy(() => import('src/pages/profile'));
const Users = lazy(() => import('src/pages/users'));
const Edit = lazy(() => import('src/pages/edit'));
const WorkspaceFolder = lazy(
  () => import('src/pages/workspace/workspaceFolder')
);
const Dictionary = lazy(() => import('src/pages/dictionary'));
const LegalCodes = lazy(() => import('src/pages/legalCodes'));
const Notifications = lazy(() => import('src/pages/notifications'));
const Companies = lazy(() => import('src/pages/companies'));
const License = lazy(() => import('src/pages/license'));
const Logging = lazy(() => import('src/pages/logging'));
const Trash = lazy(() => import('src/pages/trash'));
const Archive = lazy(() => import('src/pages/archive'));
const Audit = lazy(() => import('src/pages/audit'));
const AccountReady = lazy(() => import('src/pages/accountReady'));
const ManageBilling = lazy(() => import('src/pages/manageBilling'));
const SsoSettings = lazy(() => import('src/pages/ssoSettings'));
// Public, flag ENABLE_INVOICE_SIGNUP (the page is a 404 while it is off).
const InvoiceSignup = lazy(() => import('src/pages/invoiceSignup'));
// Public, backend flag ENABLE_FREE_MONTH (both pages are a 404 while it is off).
const StartFreeMonth = lazy(() => import('src/pages/startFreeMonth'));
const StartFreeMonthConfirm = lazy(() => import('src/pages/startFreeMonthConfirm'));
const InvoiceSignupConfirm = lazy(() => import('src/pages/invoiceSignupConfirm'));
const ZgwSettings = lazy(() => import('src/pages/zgwSettings'));
const Usage = lazy(() => import('src/pages/usage'));

type getPageType = (
  PageComponent: FC,
  TemplateComponent?: FC,
  templateProps?: any
) => JSX.Element;

const getPage: getPageType = (
  PageComponent,
  TemplateComponent = DashboardTemplate,
  templateProps
) => (
  <TemplateComponent {...templateProps}>
    <Suspense fallback={<PageLoading />}>
      <PageComponent />
    </Suspense>
  </TemplateComponent>
);

const Router: FC = () => {
  return (
    <BrowserRouter>
      <RouterContent />
    </BrowserRouter>
  );
};

const RouterContent: FC = () => {
  const isDemoAuthBootstrapping = useDemoAuthBootstrap();

  if (isDemoAuthBootstrapping) return <PageLoading />;

  return (
    <PasswordSetupGate>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/login/sso" element={<SsoCallback />} />
      <Route path="/forgotPassword/*" element={<ForgotPassword />} />
      <Route path="/confirm-email" element={<ConfirmEmail />} />
      <Route
        path="/account-ready"
        element={
          <Suspense fallback={<PageLoading />}>
            <AccountReady />
          </Suspense>
        }
      />
      {['/betalen-op-factuur', '/pay-by-invoice'].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <Suspense fallback={<PageLoading />}>
              <InvoiceSignup />
            </Suspense>
          }
        />
      ))}
      {['/betalen-op-factuur/bevestigen', '/pay-by-invoice/confirm'].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <Suspense fallback={<PageLoading />}>
              <InvoiceSignupConfirm />
            </Suspense>
          }
        />
      ))}
      <Route
        path="/start"
        element={
          <Suspense fallback={<PageLoading />}>
            <StartFreeMonth />
          </Suspense>
        }
      />
      <Route
        path="/start/confirm"
        element={
          <Suspense fallback={<PageLoading />}>
            <StartFreeMonthConfirm />
          </Suspense>
        }
      />
      <Route path="/" element={<PrivateRoute />}>
        <Route
          path="/"
          element={<DashboardTemplate children={<Dashboard />} />}
        />
        <Route path="/createTasks" element={getPage(CreateTasks)} />
        <Route
          path="/edit/:id"
          element={getPage(Edit, DashboardTemplate, {
            navbarComponent: <EditPageHeader />,
          })}
        />
        <Route path="/profile" element={getPage(Profile)} />
        <Route path="/users" element={getPage(Users)} />
        <Route path="/workspaces" element={getPage(Workspaces)} />
        <Route
          path="/workspaces/:workspaceId"
          element={getPage(WorkspacesDetails)}
        />
        <Route
          path="/workspaces/:workspaceId/:folderId"
          element={getPage(WorkspaceFolder)}
        />
        <Route path="/dictionary" element={getPage(Dictionary)} />
        <Route path="/legal-codes" element={getPage(LegalCodes)} />
        <Route path="/notifications" element={getPage(Notifications)} />
        <Route path="/companies" element={getPage(Companies)} />
        <Route path="/license" element={getPage(License)} />
        <Route path="/logging" element={getPage(Logging)} />
        <Route path="/trash" element={getPage(Trash)} />
        <Route path="/archive" element={getPage(Archive)} />
        <Route path="/audit" element={getPage(Audit)} />
        <Route path="/manage-billing" element={getPage(ManageBilling)} />
        <Route path="/sso-settings" element={getPage(SsoSettings)} />
        <Route path="/zgw-settings" element={getPage(ZgwSettings)} />
        <Route path="/usage" element={getPage(Usage)} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
    </PasswordSetupGate>
  );
};

export default Router;
